import { ClaudePromptPanel } from '@/components/admin/npc/launch/claude-prompt-panel';
import { LaunchEstimate } from '@/components/admin/npc/launch/launch-estimate';
import { useLaunchOptions } from '@/components/admin/npc/launch/use-launch-options';
import {
  VersionedDialog,
  VersionedDialogSection,
} from '@/components/admin/versioned/versioned-dialog';
import { useAdminSession } from '@/lib/admin-session';
import { estimateNpcLaunch, launchNpcSeries } from '@/lib/api';
import {
  type LaunchDraft,
  type LaunchTarget,
  OPENING_LAUNCH_DRAFT,
  launchButtonLabel,
  launchInput,
  launchRefusalOf,
  modelsInForceLabel,
  modelsVersionOptions,
  passesOf,
  passesSentence,
  playerModelChoices,
  playerSeatOf,
  sideVersionLabel,
} from '@/lib/npc-launch';
import { modelName } from '@/lib/npc-replays-labels';
import { REPLAY_MODE_SENTENCE } from '@/lib/replay-mark';
import { EMPTY_SELECT_VALUE, fromSelectValue, toSelectValue } from '@/lib/select-value';
import { versionedDay } from '@/lib/versioned-labels';
import type {
  NpcLaunch,
  NpcSeriesLaunched,
  ReplayMode,
  VersionTag,
} from '@matter_extractor/shared';
import {
  NPC_PASSES_MAX,
  NPC_PASSES_MIN,
  REPLAY_DEFAULT_PLAYER_SEAT,
} from '@matter_extractor/shared';
import { Badge, Select, TextField } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useId, useState } from 'react';

/**
 * « Lancer un rejeu » (npc.md §6, M4 ; maquette `Lancement`, variante A) : la boîte, sur un voile,
 * ouverte depuis la fiche d'un NPC ou depuis « Lancer » sur un banc.
 *
 * Ses champs, dans l'ordre de §6.1 : quoi (ce qu'on a ouvert), expérience, côté avec sa version
 * résolue, mode, passes, joueur (le serveur et son modèle, remplaçable pour ce lancement seulement
 * - ou Claude, préparé), Modèles du dispositif (ceux en vigueur ou une version de l'historique),
 * puis l'estimation de §6.2 qui se relit à chaque changement et le bouton qui dit combien de
 * rejeux partent.
 *
 * ## Rien n'est écrit avant « Lancer »
 *
 * L'estimation est un POST sans effet ; le lancement est un autre POST, et c'est lui seul qui crée
 * la série. Un refus (`no-protocol-page`, `bench-empty`...) est dit dans la boîte, qui reste
 * ouverte avec ses choix. Réussi, un lancement joué par le serveur MÈNE à la page de la série ; un
 * lancement préparé pour Claude montre la phrase à coller (§8) et ne ferme pas avant qu'on l'ait
 * vue.
 *
 * ## Les défauts sont lus, pas écrits
 *
 * L'expérience par défaut est la première qui a un protocole ; le modèle du joueur est celui de
 * Réglages > Rejeu (`REPLAY_DEFAULT_PLAYER_SEAT` tant qu'il ne répond pas) ; les Modèles sont ceux
 * en vigueur du côté choisi. Le brouillon de la boîte ne porte donc que ce que l'opérateur a CHOISI,
 * et `null` ou `''` veut dire « le défaut » : un défaut qui bouge sous la boîte ouverte se lit tel
 * qu'il est au moment où l'on lance.
 */

export interface NpcLaunchDialogProps {
  target: LaunchTarget;
  onClose: () => void;
}

/** The query and the mutation only run once the box has a body: this is the guard they share. */
function requireInput(input: NpcLaunch | null): NpcLaunch {
  if (input === null) throw new Error('Le lancement n’a pas encore de corps.');
  return input;
}

const LEGEND = 'font-semibold text-gray-600 text-caption uppercase tracking-wide';

export function NpcLaunchDialog({ target, onClose }: NpcLaunchDialogProps) {
  const { email } = useAdminSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const options = useLaunchOptions();
  const ids = {
    sideGroup: useId(),
    modeGroup: useId(),
    playerGroup: useId(),
  };
  const [draft, setDraft] = useState<LaunchDraft>(OPENING_LAUNCH_DRAFT);
  const [passesText, setPassesText] = useState(String(NPC_PASSES_MIN));
  const [launched, setLaunched] = useState<NpcSeriesLaunched | null>(null);

  const experiences = (options.experiences?.experiences ?? []).filter(
    (card) => card.retiredAt === null,
  );
  const defaultExperience =
    experiences.find((card) => card.protocolName !== null) ?? experiences[0];
  const experienceId =
    draft.experienceId === '' ? (defaultExperience?.id ?? '') : draft.experienceId;
  const experience = experiences.find((card) => card.id === experienceId);

  const baseSeat = options.settings?.player ?? REPLAY_DEFAULT_PLAYER_SEAT;
  const choices = playerModelChoices(baseSeat, options.catalog?.entries ?? []);
  const seat = playerSeatOf(draft, baseSeat, options.catalog?.entries ?? []);
  const passes = passesOf(passesText);
  const input =
    passes === null ? null : launchInput(target, { ...draft, experienceId, passes }, seat);

  const estimate = useQuery({
    queryKey: ['npc-launch-estimate', email, input],
    queryFn: () => estimateNpcLaunch(requireInput(input)),
    enabled: input !== null,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
  const versionOf = new Map(
    (estimate.data?.lines ?? []).map((line) => [line.name, line.versionNumber] as const),
  );
  const estimateRefusal = estimate.isError ? launchRefusalOf(estimate.error) : null;

  const launch = useMutation({
    mutationFn: () => launchNpcSeries(requireInput(input)),
    onSuccess: (series) => {
      void queryClient.invalidateQueries({ queryKey: ['npc-series'] });
      void queryClient.invalidateQueries({ queryKey: ['npc-benches'] });
      void queryClient.invalidateQueries({ queryKey: ['npc-replays'] });
      if (series.player.kind === 'server') {
        void navigate({ to: '/admin/npc/series/$id', params: { id: series.id } });
        onClose();
      } else {
        setLaunched(series);
      }
    },
  });

  const patch = (change: Partial<LaunchDraft>) => {
    launch.reset();
    setDraft((was) => ({ ...was, ...change }));
  };

  const experienceVersions = experience === undefined ? null : experience;
  const sideBadge = (tag: VersionTag): string | null =>
    experienceVersions === null
      ? null
      : sideVersionLabel(tag, {
          development: experienceVersions.development.number,
          production: experienceVersions.production?.number ?? null,
        });

  /* ------------------------------------------------ the series, prepared for Claude */
  if (launched !== null && launched.claudePrompt !== null) {
    return (
      <VersionedDialog
        data-testid="npc-lancement"
        title="Série préparée pour Claude"
        description={`${launched.target.kind === 'bench' ? launched.target.benchName : launched.target.npcName} · ${launched.experienceName}${launched.experienceVersionNumber === null ? '' : ` v${launched.experienceVersionNumber}`} · ${launched.mode === 'strict' ? 'strict' : 'complété'} · ${launched.passes} ${launched.passes > 1 ? 'passes' : 'passe'}`}
        confirmLabel="Ouvrir la série"
        cancelLabel="Fermer"
        busy={false}
        error={null}
        onConfirm={() => {
          void navigate({ to: '/admin/npc/series/$id', params: { id: launched.id } });
          onClose();
        }}
        onCancel={onClose}
      >
        <div data-testid="npc-lancement-claude" className="space-y-3">
          <p className="text-gray-800 text-small">
            La série est créée, vide. Collez cette phrase dans Claude Code ou claude.ai, avec le
            connecteur du dev :
          </p>
          <ClaudePromptPanel prompt={launched.claudePrompt} testId="npc-lancement" />
          <p className="text-caption text-gray-600 leading-4">
            Gratuit côté joueur : l’abonnement de l’opérateur. Les rejeux arrivent dans la série
            quand Claude les démarre ; la page de la série se rafraîchit seule.
          </p>
        </div>
      </VersionedDialog>
    );
  }

  const refusalOfLaunch = launch.isError ? launchRefusalOf(launch.error) : null;
  const launchError = launch.isError
    ? refusalOfLaunch === null
      ? 'Le lancement a échoué. Réessayez dans un instant.'
      : `${refusalOfLaunch.title} : ${refusalOfLaunch.sentence}`
    : null;
  const replays = estimate.data?.replays ?? null;
  const blocked =
    input === null || estimateRefusal !== null || experience === undefined || estimate.isPending;

  return (
    <VersionedDialog
      data-testid="npc-lancement"
      title="Lancer un rejeu"
      description={
        target.kind === 'bench'
          ? 'Chaque NPC du banc joue l’expérience choisie, à sa version en vigueur. La série se suit sur sa page, et ses rejeux sont des sessions comme les autres.'
          : 'Ce NPC joue l’expérience choisie, à sa version en vigueur. La série se suit sur sa page, et son rejeu est une session comme les autres.'
      }
      confirmLabel={launchButtonLabel(replays)}
      busy={launch.isPending}
      error={launchError}
      confirmDisabled={blocked}
      onConfirm={() => launch.mutate()}
      onCancel={onClose}
    >
      <div className="space-y-4">
        <VersionedDialogSection title="Quoi">
          <div
            data-testid="npc-lancement-quoi"
            className="space-y-2 rounded-md border-2 border-gray-200 bg-gray-50 px-3 py-2.5"
          >
            {target.kind === 'npc' ? (
              <p className="text-gray-900 text-small">
                Le NPC <b className="font-semibold text-bleu-nuit">{target.name}</b>
              </p>
            ) : (
              <>
                <p className="text-gray-900 text-small">
                  Le banc <b className="font-semibold text-bleu-nuit">{target.name}</b> ·{' '}
                  {target.npcNames.length} NPC
                </p>
                <ul className="flex flex-wrap gap-1.5">
                  {target.npcNames.map((name) => (
                    <li
                      key={name}
                      className="rounded-full border border-gray-300 bg-white px-2 py-px text-caption text-gray-700"
                    >
                      {versionOf.has(name) ? `${name} · v${versionOf.get(name)}` : name}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <p className="text-caption text-gray-600">
              La série fige {target.kind === 'bench' ? 'cette liste et ' : ''}la version de chaque
              NPC au lancement.
            </p>
          </div>
        </VersionedDialogSection>

        {/* The test id sits on a wrapper: the package `Select` takes none (its
            trigger is the `combobox` inside). An empty library is the select's
            placeholder, the one value `''` Radix keeps for « nothing chosen ». */}
        <div data-testid="npc-lancement-experience">
          <Select
            label="Expérience"
            value={experienceId}
            disabled={experiences.length === 0}
            placeholder={
              options.experiencesFailed ? 'Les expériences n’ont pas pu être lues' : 'Chargement...'
            }
            onValueChange={(value) => patch({ experienceId: value })}
            options={experiences.map((card) => ({
              value: card.id,
              label: card.protocolName === null ? `${card.name} (sans conversation)` : card.name,
            }))}
          />
        </div>

        <fieldset className="space-y-1.5">
          <legend className={LEGEND}>Côté</legend>
          {(['development', 'production'] as const).map((tag) => {
            const badge = sideBadge(tag);
            return (
              <label key={tag} className="flex items-center gap-2 text-gray-900 text-small">
                <input
                  type="radio"
                  name={ids.sideGroup}
                  data-testid={`npc-lancement-cote-${tag}`}
                  checked={draft.tag === tag}
                  onChange={() => patch({ tag, modelsVersionId: null })}
                  className="size-4 accent-bleu-nuit"
                />
                <span>
                  {tag === 'development' ? (
                    <>
                      Développement <span className="text-gray-600">(latest dev)</span>
                    </>
                  ) : (
                    <>
                      Production{' '}
                      <span className="text-gray-600">(latest prod, joué sur le dev)</span>
                    </>
                  )}
                </span>
                {badge === null ? null : <Badge tone="accent">{badge}</Badge>}
              </label>
            );
          })}
        </fieldset>

        <fieldset className="space-y-1.5">
          <legend className={LEGEND}>Mode</legend>
          {(['strict', 'completed'] as ReplayMode[]).map((mode) => (
            <label key={mode} className="flex items-center gap-2 text-gray-900 text-small">
              <input
                type="radio"
                name={ids.modeGroup}
                data-testid={`npc-lancement-mode-${mode}`}
                checked={draft.mode === mode}
                onChange={() => patch({ mode })}
                className="size-4 accent-bleu-nuit"
              />
              <span>
                <b className="font-semibold">{mode === 'strict' ? 'Strict' : 'Complété'}</b> -{' '}
                {REPLAY_MODE_SENTENCE[mode]}
              </span>
            </label>
          ))}
        </fieldset>

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-20 shrink-0">
            <TextField
              label="Passes"
              data-testid="npc-lancement-passes"
              type="number"
              inputMode="numeric"
              min={NPC_PASSES_MIN}
              max={NPC_PASSES_MAX}
              value={passesText}
              onChange={(event) => {
                launch.reset();
                setPassesText(event.currentTarget.value);
              }}
            />
          </div>
          <div className="pb-3">
            <p data-testid="npc-lancement-passes-phrase" className="text-caption text-gray-600">
              {passes === null
                ? `De ${NPC_PASSES_MIN} à ${NPC_PASSES_MAX} passes.`
                : passesSentence(target, passes)}
            </p>
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className={LEGEND}>Joueur</legend>
          <div
            className={`space-y-2 rounded-md border-2 px-3 py-2.5 ${draft.player === 'server' ? 'border-bleu-nuit bg-bleu-nuit-tint' : 'border-gray-300 bg-white'}`}
          >
            <label className="flex items-center gap-2 text-gray-900 text-small">
              <input
                type="radio"
                name={ids.playerGroup}
                data-testid="npc-lancement-joueur-serveur"
                checked={draft.player === 'server'}
                onChange={() => patch({ player: 'server' })}
                className="size-4 accent-bleu-nuit"
              />
              <span>
                <b className="font-semibold">Joué par le serveur</b> - payant, compté sur chaque
                rejeu
              </span>
            </label>
            {draft.player === 'server' ? (
              <div className="space-y-1 pl-6">
                <div
                  data-testid="npc-lancement-modele-joueur"
                  className="w-fit min-w-60 max-w-full"
                >
                  <Select
                    label="Modèle du joueur"
                    compact
                    value={draft.playerModel ?? baseSeat.model}
                    onValueChange={(playerModel) => patch({ playerModel })}
                    options={choices.map((choice) => ({
                      value: choice.model,
                      label: choice.label,
                    }))}
                  />
                </div>
                <p className="text-caption text-gray-600 leading-4">
                  Pour ce lancement seulement : le réglage de Réglages &gt; Rejeu ne bouge pas.
                  Chaque rejeu fige le modèle qui l’a joué.
                </p>
              </div>
            ) : null}
          </div>
          <div
            className={`space-y-1 rounded-md border-2 px-3 py-2.5 ${draft.player === 'claude' ? 'border-bleu-nuit bg-bleu-nuit-tint' : 'border-gray-300 bg-white'}`}
          >
            <label className="flex items-center gap-2 text-gray-900 text-small">
              <input
                type="radio"
                name={ids.playerGroup}
                data-testid="npc-lancement-joueur-claude"
                checked={draft.player === 'claude'}
                onChange={() => patch({ player: 'claude' })}
                className="size-4 accent-bleu-nuit"
              />
              <span>
                <b className="font-semibold">Préparé pour Claude</b> - gratuit côté joueur
              </span>
            </label>
            <p className="pl-6 text-caption text-gray-600 leading-4">
              La série se crée vide et l’app vous donne une phrase à coller dans Claude Code ou
              claude.ai, avec le connecteur du dev. Les rejeux arrivent quand Claude les démarre.
            </p>
          </div>
        </fieldset>

        <div data-testid="npc-lancement-modeles">
          <Select
            label="Modèles du dispositif"
            value={toSelectValue(draft.modelsVersionId ?? '')}
            onValueChange={(value) => {
              const id = fromSelectValue(value);
              patch({ modelsVersionId: id === '' ? null : id });
            }}
            options={[
              {
                value: EMPTY_SELECT_VALUE,
                label:
                  options.models === undefined
                    ? 'Ceux en vigueur'
                    : modelsInForceLabel(options.models, draft.tag),
              },
              ...(options.models === undefined
                ? []
                : modelsVersionOptions(options.models, draft.tag, versionedDay).map((option) => ({
                    value: option.id,
                    label: option.label,
                  }))),
            ]}
            help="Ou une version de l’historique de Réglages > Modèles, pour ce lancement seulement : les autres sessions du dev gardent les Modèles en vigueur."
          />
        </div>

        <LaunchEstimate
          estimate={estimate.data}
          refusal={estimateRefusal}
          failed={estimate.isError && estimateRefusal === null}
          pending={estimate.isFetching && estimate.data === undefined}
          player={draft.player}
          experienceName={experience?.name ?? 'cette expérience'}
          playerModel={modelName(draft.playerModel ?? baseSeat.model)}
        />
      </div>
    </VersionedDialog>
  );
}
